import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { authorizedRequest } from '../../api/authorizedRequest';
import { useSearchParams } from 'react-router-dom';

// Define Interfaces
interface EmployeeDto {
  ssn: string;
  name: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
}

interface EmployeeCheckDto {
  checkNumber: string;
  date: string;
  endDate: string;
  grossPay: number;
  netPay: number;
}

interface DirectDepositDto {
  description: string;
  amount: number;
  ytd: number;
}

interface EarningDto {
  description: string;
  units: number;
  amount: number;
  ytd: number;
}

interface GetTaxOutputDto {
  description: string;
  amount: number;
  ytd: number;
}

interface GetOtherDeductionOutputDto {
  description: string;
  amount: number;
  ytd: number;
}

interface GetEarningsDetailOutputDto {
  payId: string;
  rate: number;
  units: number;
  job: string;
  jcCostCode: string;
  amount: number;
  periodEndDate: string;
  workDate: string;
  payType: string;
  jobName: string;
}

interface PagedResultDto<T> {
  totalCount: number;
  items: T[];
}

const EmployeeCheckDetails: React.FC = () => {
  const [searchParams] = useSearchParams();
  const checkdate = searchParams.get('checkdate');
  const enddate = searchParams.get('enddate');
  const sequence = searchParams.get('sequence');

  const [employee, setEmployee] = useState<EmployeeDto | null>(null);
  const [check, setCheck] = useState<EmployeeCheckDto | null>(null);
  const [directDeposits, setDirectDeposits] = useState<PagedResultDto<DirectDepositDto>>({ totalCount: 0, items: [] });
  const [earnings, setEarnings] = useState<PagedResultDto<EarningDto>>({ totalCount: 0, items: [] });
  const [taxes, setTaxes] = useState<PagedResultDto<GetTaxOutputDto>>({ totalCount: 0, items: [] });
  const [otherDeductions, setOtherDeductions] = useState<PagedResultDto<GetOtherDeductionOutputDto>>({ totalCount: 0, items: [] });
  const [earningsDetail, setEarningsDetail] = useState<PagedResultDto<GetEarningsDetailOutputDto>>({ totalCount: 0, items: [] });

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const [
          employeeResponse,
          checkResponse,
          directDepositsResponse,
          earningsResponse,
          taxesResponse,
          otherDeductionsResponse,
          earningsDetailResponse,
        ] = await Promise.all([
          authorizedRequest({ url: '/Employee/GetCurrentUser', method: 'GET' }),
          authorizedRequest({ url: `/Employee/check?checkdate=${checkdate}&enddate=${enddate}&sequence=${sequence}`, method: 'GET' }),
          authorizedRequest({ url: `/Employee/DirectDeposits?checkDate=${checkdate}&endDate=${enddate}&sequence=${sequence}`, method: 'GET' }),
          authorizedRequest({ url: `/Employee/Earnings?checkDate=${checkdate}&endDate=${enddate}&sequence=${sequence}`, method: 'GET' }),
          authorizedRequest({ url: `/Employee/Taxes?checkDate=${checkdate}&endDate=${enddate}&sequence=${sequence}`, method: 'GET' }),
          authorizedRequest({ url: `/Employee/OtherDeduction?checkDate=${checkdate}&endDate=${enddate}&sequence=${sequence}`, method: 'GET' }),
          authorizedRequest({ url: `/Employee/EarningsDetail?checkDate=${checkdate}&endDate=${enddate}&sequence=${sequence}`, method: 'GET' }),
        ]);

        setEmployee(employeeResponse.data);
        setCheck(checkResponse.data);
        setDirectDeposits(directDepositsResponse.data);
        setEarnings(earningsResponse.data);
        setTaxes(taxesResponse.data);
        setOtherDeductions(otherDeductionsResponse.data);
        setEarningsDetail(earningsDetailResponse.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, [checkdate, enddate, sequence]);

  const handlePrint = () => {
    const printContent = document.getElementById('printdiv');
    if (printContent) {
      const originalContents = document.body.innerHTML;
      document.body.innerHTML = printContent.innerHTML;
      window.print();
      document.body.innerHTML = originalContents;
      window.location.reload();
    }
  };

  const directDepositColumns = [
    {
      name: 'Account Information',
      selector: (row: DirectDepositDto) => row.description,
      width: '600px',
    },
    {
      name: 'This Check',
      selector: (row: DirectDepositDto) => row.amount.toFixed(2),
      width: '100px',
    },
    {
      name: 'YTD',
      selector: (row: DirectDepositDto) => row.ytd.toFixed(2),
      width: '100px',
    },
  ];

  const earningsColumns = [
    { name: 'Description', selector: (row: EarningDto) => row.description },
    { name: 'Hours/Unit', selector: (row: EarningDto) => row.units },
    { name: 'This Check', selector: (row: EarningDto) => row.amount.toFixed(2) },
    { name: 'YTD', selector: (row: EarningDto) => row.ytd.toFixed(2) },
  ];

  const earningsDetailColumns = [
    { name: 'PayId', selector: (row: GetEarningsDetailOutputDto) => row.payId },
    { name: 'Work Date', selector: (row: GetEarningsDetailOutputDto) => row.workDate ? new Date(row.workDate).toLocaleDateString() : '' },
    { name: 'Hours/Unit', selector: (row: GetEarningsDetailOutputDto) => row.units },
    { name: 'Rate', selector: (row: GetEarningsDetailOutputDto) => row.rate.toFixed(2) },
    { name: 'Amount', selector: (row: GetEarningsDetailOutputDto) => row.amount.toFixed(2) },
    { name: 'Job', selector: (row: GetEarningsDetailOutputDto) => row.job },
    { name: 'Cost Code', selector: (row: GetEarningsDetailOutputDto) => row.jcCostCode },
  ];

  const taxesColumns = [
    { name: 'Taxes', selector: (row: GetTaxOutputDto) => row.description },
    { name: 'This Check', selector: (row: GetTaxOutputDto) => row.amount.toFixed(2) },
    { name: 'YTD', selector: (row: GetTaxOutputDto) => row.ytd.toFixed(2) },
  ];

  const otherDeductionsColumns = [
    { name: 'Description', selector: (row: GetOtherDeductionOutputDto) => row.description },
    { name: 'This Check', selector: (row: GetOtherDeductionOutputDto) => row.amount.toFixed(2) },
    { name: 'YTD', selector: (row: GetOtherDeductionOutputDto) => row.ytd.toFixed(2) },
  ];

  if (isLoading) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="spinner-border text-primary" role="status" style={{ width: '4rem', height: '4rem' }}></div>
        <div className="mt-3">Fetching  details. Please wait...</div>
      </div>
    );
  }

  if (!employee || !check) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="alert alert-danger mt-3">Unable to load employee or check details.</div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-sm-6">
          <div className="card border-dark">
            <div className="card-body text-dark">
              <h5 className="card-title">Employee Details</h5>
              <p className="card-text"><b>SSN: </b>XXX-XX-{employee.ssn}</p>
              <p className="card-text">{employee.name}</p>
              <p className="card-text">Address 1 : {employee.address1}</p>
              <p className="card-text">Address 2: {employee.address2}</p>
              <p className="card-text">{employee.city}, {employee.state} {employee.zip}</p>
            </div>
          </div>
        </div>
        <div className="col-sm-6">
          <div className="card border-dark">
            <div className="card-body text-dark">
              <h5 className="card-title">Check Details</h5>
              <p className="card-text"><b>Check Number: </b>{check.checkNumber}</p>
              <p className="card-text"><b>Check Date: </b>{new Date(check.date).toLocaleDateString()}</p>
              <p className="card-text"><b>Payroll Period End Date: </b>{new Date(check.endDate).toLocaleDateString()}</p>
              <p className="card-text"><b>Gross Pay: </b>{check.grossPay.toFixed(2)}</p>
              <p className="card-text"><b>Net Pay: </b>{check.netPay.toFixed(2)}</p>
              <p className="card-text"></p>
            </div>
          </div>
        </div>
      </div>

      <div className="text-center mt-4">
              <h5 className="card-header d-flex justify-content-between align-items-center">
                  Direct Deposits
                  <button className="btn btn-secondary" type="button" onClick={handlePrint}>
                      <i className="fa fa-print"></i>
                  </button>
              </h5>
              <div className="card-body">
                  <DataTable
                      columns={directDepositColumns}
                      data={directDeposits.items}
                  />
              </div>
          </div>

          <div className="text-center mt-4">
              <h5 className="card-header d-flex justify-content-between align-items-center">
                  Earnings
              </h5>
              <div className="card-body">
                  <DataTable
                      columns={earningsColumns}
                      data={earnings.items}
                  />
              </div>
          </div>

          <div className="text-center mt-4">
              <div className="card-header">Earnings Details</div>
              <div className="card-body">
                  <DataTable
                      columns={earningsDetailColumns}
                      data={earningsDetail.items}
                  />
              </div>
          </div>

          <div className="text-center mt-4">
              <div className="card-header">Taxes</div>
              <div className="card-body">
                  <DataTable
                      columns={taxesColumns}
                      data={taxes.items}
                  />
              </div>
          </div>

          <div className="text-center mt-4">
              <div className="card-header">Other Deductions</div>
              <div className="card-body">
                  <DataTable
                      columns={otherDeductionsColumns}
                      data={otherDeductions.items}
                  />
              </div>
          </div>

          {/* Printable content */}
          <div style={{ display: 'none' }}>
              <div className="page" id="printdiv">
                  <table className="table table-borderless">
                      <tbody>
                          <tr>
                              <td><h2 style={{ textAlign: 'center' }}> Site Development, Inc.</h2></td>
                          </tr>
                          <tr>
                              <td><h3 style={{ textAlign: 'center' }}>Employee Information:</h3></td>
                          </tr>
                      </tbody>
                  </table>
                  <table className="table table-sm table-borderless">
                      <tbody>
                          <tr>
                              <td style={{ width: '20%' }}>Name:</td>
                              <td style={{ width: '30%' }}>{employee.name}</td>
                              <td style={{ width: '20%' }}>Address:</td>
                              <td style={{ width: '30%' }}>{employee.address1} <br /> {employee.city}, {employee.state} {employee.zip}</td>
                          </tr>
                          <tr>
                              <td style={{ width: '20%' }}>Check Number:</td>
                              <td style={{ width: '30%' }}>{check.checkNumber}</td>
                              <td style={{ width: '20%' }}>&nbsp;</td>
                              <td style={{ width: '30%' }}>&nbsp;</td>
                          </tr>
                          <tr>
                              <td style={{ width: '20%' }}>Check Date:</td>
                              <td style={{ width: '30%' }}>{new Date(check.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')}
                              </td>
                              <td style={{ width: '20%' }}>Payroll Period End Date:</td>
                              <td style={{ width: '30%' }}>{new Date(check.endDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')}
                              </td>
                          </tr>
                          <tr>
                              <td style={{ width: '20%' }}>Gross Pay:</td>
                              <td style={{ width: '30%' }}>{check.grossPay.toFixed(2)}</td>
                              <td style={{ width: '20%' }}>Net Pay:</td>
                              <td style={{ width: '30%' }}>{check.netPay.toFixed(2)}</td>
                          </tr>
                      </tbody>
                  </table>
                  <table className="table">
                      <thead>
                          <tr>
                              <th scope="col" style={{ width: '60%', textAlign: 'center' }} colSpan={3}>
                                  <h4>Direct Deposit</h4>
                              </th>
                          </tr>
                          <tr>
                              <th scope="col" style={{ width: '60%' }}>Account Information</th>
                              <th scope="col" style={{ width: '20%' }}>This Check</th>
                              <th scope="col" style={{ width: '20%' }}>YTD</th>
                          </tr>
                      </thead>
                      <tbody>
                          {directDeposits.items.map((row, index) => (
                              <tr key={index}>
                                  <td scope="row" style={{ width: '60%' }}>{row.description}</td>
                                  <td scope="row" style={{ width: '20%' }}>{row.amount.toFixed(2)}</td>
                                  <td scope="row" style={{ width: '20%' }}>{row.ytd.toFixed(2)}</td>
                              </tr>
                          ))}
                      </tbody>
                  </table>
                  <table className="table">
                      <thead>
                          <tr>
                              <th scope="col" style={{ width: '60%', textAlign: 'center' }} colSpan={3}>
                                  <h4>Earnings</h4>
                              </th>
                          </tr>
                          <tr>
                              <th scope="col" style={{ width: '60%' }}>Description</th>
                              <th scope="col" style={{ width: '20%' }}>This Check</th>
                              <th scope="col" style={{ width: '20%' }}>YTD</th>
                          </tr>
                      </thead>
                      <tbody>
                          {earnings.items.map((row, index) => (
                              <tr key={index}>
                                  <td scope="row" style={{ width: '60%' }}>{row.description}</td>
                                  <td scope="row" style={{ width: '20%' }}>{row.amount.toFixed(2)}</td>
                                  <td scope="row" style={{ width: '20%' }}>{row.ytd.toFixed(2)}</td>
                              </tr>
                          ))}
                      </tbody>
                  </table>
                  <table className="table">
                      <thead>
                          <tr>
                              <th scope="col" style={{ width: '60%', textAlign: 'center' }} colSpan={3}>
                                  <h4>Taxes</h4>
                              </th>
                          </tr>
                          <tr>
                              <th scope="col" style={{ width: '60%' }}>Description</th>
                              <th scope="col" style={{ width: '20%' }}>This Check</th>
                              <th scope="col" style={{ width: '20%' }}>YTD</th>
                          </tr>
                      </thead>
                      <tbody>
                          {taxes.items.map((row, index) => (
                              <tr key={index}>
                                  <td scope="row" style={{ width: '60%' }}>{row.description}</td>
                                  <td scope="row" style={{ width: '20%' }}>{row.amount.toFixed(2)}</td>
                                  <td scope="row" style={{ width: '20%' }}>{row.ytd.toFixed(2)}</td>
                              </tr>
                          ))}
                      </tbody>
                  </table>
                  <table className="table">
                      <thead>
                          <tr>
                              <th scope="col" style={{ width: '60%', textAlign: 'center' }} colSpan={3}>
                                  <h4>Other Deductions</h4>
                              </th>
                          </tr>
                          <tr>
                              <th scope="col" style={{ width: '60%' }}>Description</th>
                              <th scope="col" style={{ width: '20%' }}>This Check</th>
                              <th scope="col" style={{ width: '20%' }}>YTD</th>
                          </tr>
                      </thead>
                      <tbody>
                          {otherDeductions.items.map((row, index) => (
                              <tr key={index}>
                                  <td scope="row" style={{ width: '60%' }}>{row.description}</td>
                                  <td scope="row" style={{ width: '20%' }}>{row.amount.toFixed(2)}</td>
                                  <td scope="row" style={{ width: '20%' }}>{row.ytd.toFixed(2)}</td>
                              </tr>
                          ))}
                      </tbody>
                  </table>
              </div>
          </div>
      </div>
  );
};

export default EmployeeCheckDetails;
